import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, tap } from 'rxjs';

import { AuthService, LOGIN_ROUTE } from '@core/services/auth.service';
import { NotificationService } from '@core/services/notification.service';

import { AuthRepositoryContract } from '../repositories/auth.repository.contract';
import { AuthStore } from '../store/auth.store';

/** Where a signed-out visitor lands: the login screen. */
export const POST_LOGOUT_ROUTE = LOGIN_ROUTE;

@Injectable()
export class LogoutUseCase {
  private readonly repository = inject(AuthRepositoryContract);
  private readonly store = inject(AuthStore);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notify = inject(NotificationService);

  /**
   * Tells the API first — while the bearer is still attached — then drops
   * the local session and leaves. The order matters for the token: once
   * clearSession() runs, no call can identify the session being ended. The
   * outcome does not: a failed or unreachable API still signs the visitor
   * out locally, which is the safer result, so the stream never errors.
   */
  execute(): Observable<void> {
    return this.repository.logout().pipe(
      // The API's own confirmation line ("Logged out."), so a successful
      // server-side sign-out is visible; a failed one stays quiet.
      tap((message) => this.notify.success(message || 'You have been logged out.')),
      catchError(() => of(undefined)),
      map(() => undefined),
      finalize(() => {
        this.store.reset();
        this.auth.clearSession();
        void this.router.navigateByUrl(POST_LOGOUT_ROUTE);
      }),
    );
  }
}
